import type { ConditionResult, AnswerKey, NodeId, QuestionId } from './types';
import type { NodeVisibility, VisibilityResult } from './visibility';

/**
 * ProgressCalculator — tellers voor de boom links en de kop van het opnamescherm.
 *
 * Tellers tellen **zichtbare** vragen; een onbereikbare vraag telt niet mee, ook
 * niet als er ooit een antwoord op gegeven is.
 */

export interface NodeProgress {
    readonly nodeId: NodeId;
    readonly visibleCount: number;
    readonly answeredCount: number;
    readonly percentage: number;
    /** Eigen vragen plus die van alle afstammelingen. */
    readonly subtreeVisibleCount: number;
    readonly subtreeAnsweredCount: number;
    readonly subtreePercentage: number;
    readonly allAnswered: boolean;
    readonly allAnsweredDeep: boolean;
    readonly firstUnansweredQuestionKey: AnswerKey | null;
    readonly result: ConditionResult;
}

export interface ProgressSnapshot {
    /** Aantal verschillende zichtbare vragen in het hele dossier. */
    readonly visibleQuestions: number;
    readonly answeredQuestions: number;
    /** Naar beneden afgerond, zodat 100% pas verschijnt als alles beantwoord is. */
    readonly percentage: number;
    readonly nodes: ReadonlyMap<NodeId, NodeProgress>;
    readonly totalNodes: number;
    readonly completedNodes: number;
    /** De eerste knoop met een openstaande vraag — "Eerst onvolledig". */
    readonly firstIncompleteNodeId: NodeId | null;
    readonly firstUnansweredQuestionKey: AnswerKey | null;
    readonly firstUnansweredQuestionId: QuestionId | null;
    readonly isComplete: boolean;
}

export function calculateProgress(
    visibility: VisibilityResult,
    results: ReadonlyMap<NodeId, ConditionResult>,
): ProgressSnapshot {
    const nodes = new Map<NodeId, NodeProgress>();
    let completedNodes = 0;

    for (let index = visibility.order.length - 1; index >= 0; index -= 1) {
        const nodeId = visibility.order[index] as NodeId;
        const node = visibility.nodes.get(nodeId) as NodeVisibility;
        let subtreeVisible = node.visibleCount;
        let subtreeAnswered = node.answeredCount;

        for (const childId of node.childIds) {
            const child = nodes.get(childId);

            if (child) {
                subtreeVisible += child.subtreeVisibleCount;
                subtreeAnswered += child.subtreeAnsweredCount;
            }
        }

        if (node.allAnswered) {
            completedNodes += 1;
        }

        nodes.set(nodeId, {
            nodeId,
            visibleCount: node.visibleCount,
            answeredCount: node.answeredCount,
            percentage: percentageOf(node.answeredCount, node.visibleCount),
            subtreeVisibleCount: subtreeVisible,
            subtreeAnsweredCount: subtreeAnswered,
            subtreePercentage: percentageOf(subtreeAnswered, subtreeVisible),
            allAnswered: node.allAnswered,
            allAnsweredDeep: node.allAnsweredDeep,
            firstUnansweredQuestionKey: node.firstUnansweredQuestionKey,
            result: results.get(nodeId) ?? null,
        });
    }

    const firstIncompleteNodeId = visibility.firstIncompleteNodeId;
    const firstIncomplete = firstIncompleteNodeId
        ? (visibility.nodes.get(firstIncompleteNodeId) as NodeVisibility)
        : null;
    const visibleQuestions = visibility.visibleQuestionKeys.size;
    const answeredQuestions = visibility.answeredQuestionKeys.size;

    return {
        visibleQuestions,
        answeredQuestions,
        percentage: percentageOf(answeredQuestions, visibleQuestions),
        nodes,
        totalNodes: visibility.order.length,
        completedNodes,
        firstIncompleteNodeId,
        firstUnansweredQuestionKey: firstIncomplete?.firstUnansweredQuestionKey ?? null,
        firstUnansweredQuestionId: firstIncomplete?.firstUnansweredQuestionId ?? null,
        isComplete: firstIncompleteNodeId === null,
    };
}

/** Gehele procenten, naar beneden afgerond; een leeg geheel telt als 100%. */
export function percentageOf(answered: number, visible: number): number {
    if (visible <= 0) {
        return 100;
    }

    return Math.floor((answered * 100) / visible);
}
